import moment from 'moment';
import { useEffect, useState } from 'react';
import { getFinancialPeriodLock } from 'src/services/financialPeriodService/getFinancialPeriodLock';

/** A financial period is one calendar month, and that is what the API is keyed by. */
const MONTH_KEY = 'YYYY-MM';

export type TFinancialPeriodLockResult = {
  /** True only on a clean answer saying finance has closed the books on this period. */
  isLocked: boolean;
  /**
   * False while the answer is still in flight. The two paths differ by a whole button, so callers
   * wait rather than render Path A and snatch the preliminary review back a moment later.
   */
  isKnown: boolean;
};

/**
 * Whether finance has locked the financial period this closeout period falls in (BOPS-170).
 *
 * Unknown resolves to **unlocked**, which is the opposite of the closed-month gate in
 * use-closed-months and deliberately so. There, withholding an action cost nothing — the backend
 * would have rejected it anyway. Here a wrong lock removes the preliminary invoice review and both
 * sign-offs from a facility that is entitled to them and, because the second sign-off is what
 * unlocks generation for finance (BOPS-137), would stall that month's invoice. Wrongly offering a
 * review of an already-invoiced month is a wasted click; wrongly denying one blocks the close.
 *
 * @param enabled skip the request entirely when the caller has nothing to gate on it — the bar is
 *                hidden altogether on an open or weekly period.
 */
export const useFinancialPeriodLock = (
  startDate?: string,
  enabled = true
): TFinancialPeriodLockResult => {
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [isKnown, setIsKnown] = useState<boolean>(false);

  //  `moment(undefined)` is *now* and reports itself valid, so a missing date has to be rejected
  //  before parsing or the screen would quietly ask about today's period.
  //
  //  Only the start date is needed: a monthly closeout is its month, and a custom range is
  //  confined to one calendar month by `validateCustomRange`, so both resolve to a single key.
  //  Formatted rather than raw so the effect re-runs on a real change of period, not on every
  //  re-render handing back a new `location.state` object.
  const start = startDate ? moment(startDate) : moment.invalid();
  const monthKey = start.isValid() ? start.format(MONTH_KEY) : '';

  useEffect(() => {
    if (!enabled) {
      setIsLocked(false);
      setIsKnown(false);
      return;
    }

    //  No period to ask about. Settle on unlocked rather than sit unknown forever, which would
    //  hold the preliminary review back on a page that is otherwise working.
    if (!monthKey) {
      setIsLocked(false);
      setIsKnown(true);
      return;
    }

    let isActive = true;
    setIsKnown(false);

    getFinancialPeriodLock(monthKey)
      .then((locked) => {
        if (!isActive) return;
        //  `null` is an unclear answer, and an unclear answer is never a lock — see above.
        setIsLocked(locked === true);
      })
      .finally(() => {
        if (isActive) setIsKnown(true);
      });

    return () => {
      isActive = false;
    };
  }, [enabled, monthKey]);

  return { isLocked, isKnown };
};

export default useFinancialPeriodLock;
